import { use } from 'react'
import {
  attachInstantCard,
  claimLimitedAccount,
  createWallet,
  linkKycRecord,
} from '@api/api'
import {
  ACCOUNT_TYPE,
  CreateWalletRequest,
  CreateWalletResponse,
} from '@api/api.types'
import { DialogContext } from '@context/DialogContext'
import { usePreKyc } from '@hooks/usePreKyc'
import { createAddressInfo } from '@lib/address'
import { fireMixpanelEvent, setAnalyticsDeeplinkUrl } from '@lib/analytics'
import { useAppNavigate } from '@routes/-utils/useAppNavigate'
import { useSuspenseMappedWalletTerms } from '@src/selectors/terms'
import { useStoreState } from '@store/index'
import { useMutation, useMutationState } from '@tanstack/react-query'

import { useOrgSettings } from './org'

export const WALLET_CREATION_MUTATION_KEY = 'WALLET_CREATION_MUTATION_KEY'
export const useCreateWallet = () => {
  const { businessAccountType } = useStoreState(state => state.onboarding)
  return useMutation({
    mutationKey: [WALLET_CREATION_MUTATION_KEY],
    mutationFn: async (params: CreateWalletRequest) => {
      const { data } = await createWallet({
        ...params,
        ...(businessAccountType ? { business_type: businessAccountType } : {}),
      })
      setAnalyticsDeeplinkUrl(data.data.login_link)
      return data
    },
    gcTime: Infinity,
  })
}

export const useClaimLimitedAccountMutation = () => {
  const navigate = useAppNavigate()
  const failKycInquiry = useStoreState(state => state.onboarding.failKycInquiry)
  const accountToken = useStoreState(state => state.onboarding.accountToken)
  const ssnLastFour = useStoreState(state => state.onboarding.ssnLastFour)

  return useMutation({
    mutationFn: () =>
      claimLimitedAccount({
        account_reference_token: accountToken,
        ssn_last_four: ssnLastFour,
        // use this to test a failing case
        debug_fail_kyc_inquiry: failKycInquiry,
      }),
    onSettled: () => {
      navigate({ to: '/kycResults' })
    },
  })
}

export const useLinkUserWithKycRecordMutation = () => {
  const { kycReferenceId, ssnLastFour } = usePreKyc()
  return useMutation({
    mutationFn: async () => {
      const requestBody = {
        kyc_reference_id: kycReferenceId,
        ssn_last_four: ssnLastFour,
      }
      await linkKycRecord(requestBody)
    },
    onError: (e: any) => {
      fireMixpanelEvent('linkKycRecord.error')
    },
  })
}

export const useCreateWalletMutation = () => {
  const navigate = useAppNavigate()
  const { data: terms = [] } = useSuspenseMappedWalletTerms()
  const { data: orgSettings } = useOrgSettings()
  const { showAlert } = use(DialogContext)
  const address = useStoreState(state => state.onboarding.enteredAddress)
  const noCardWanted = useStoreState(state => state.onboarding.noCardWanted)
  const { mutateAsync: createWalletMutation } = useCreateWallet()
  return useMutation({
    mutationFn: () => {
      const req = {
        create_physical_card: orgSettings?.data.require_physical_card_ordering
          ? true
          : !noCardWanted,
        terms,
        address: createAddressInfo(address),
      }
      return createWalletMutation(req)
    },
    onSuccess: () => {
      navigate({ to: '/accountCreated' })
    },
    onError: e => {
      showAlert({
        title: 'Error creating wallet.',
        description:
          'There was an error creating your wallet. Please try again later.',
        hideCancel: true,
      })
    },
  })
}

export const useAttachInstantCardMutation = () => {
  const navigate = useAppNavigate()
  const { nameOnCard, cardLastFour } = useStoreState(state => state.onboarding)
  return useMutation({
    mutationFn: () => {
      const req = {
        name_on_card: nameOnCard,
        last_four: cardLastFour,
      }
      return attachInstantCard(req)
    },
    onSettled: () => {
      navigate({ to: '/accountCreated' })
    },
  })
}

export const useClaimWalletMutation = () => {
  const navigate = useAppNavigate()
  const { mutateAsync: attachInstantCardMutation } =
    useAttachInstantCardMutation()
  const { showAlert } = use(DialogContext)
  const { data: orgSettings } = useOrgSettings()
  const { data: terms = [] } = useSuspenseMappedWalletTerms()

  const { enteredAddress, instantCardUserToken } = useStoreState(
    state => state.onboarding
  )
  const payload = {
    create_physical_card: orgSettings?.data.require_physical_card_ordering,
    has_existing_account: true,
    terms,
    address: createAddressInfo(enteredAddress),
  }
  const { mutateAsync: createWalletMutation } = useCreateWallet()
  return useMutation({
    mutationFn: () => createWalletMutation(payload),
    onSuccess: () => {
      if (!!instantCardUserToken) {
        return attachInstantCardMutation()
      }
      navigate({ to: '/accountCreated' })
    },
    onError: () => {
      showAlert({
        title: 'Error claiming wallet.',
        description:
          'There was an error claiming your wallet. Please try again later.',
        hideCancel: true,
      })
    },
  })
}

export const useActivateInstantCardMutation = () => {
  const navigate = useAppNavigate()
  const { showAlert } = use(DialogContext)
  const { data: orgSettings } = useOrgSettings()
  const { data: terms = [] } = useSuspenseMappedWalletTerms()
  const { instantCardUserToken, enteredAddress, birthDate, essentialFlow } =
    useStoreState(state => state.onboarding)
  const config: CreateWalletRequest = {
    claimed_account_id: instantCardUserToken,
    terms,
    address: createAddressInfo(enteredAddress),

    version: 5,
    create_physical_card: orgSettings?.data.require_physical_card_ordering
      ? true
      : undefined,
    ...(essentialFlow
      ? {
          birthdate: birthDate?.toISOString().split('T')[0] as string,
          account_type: ACCOUNT_TYPE.PAYCARD,
        }
      : {}),
  }
  const { mutateAsync: createWalletMutation } = useCreateWallet()
  const { mutate, isPending } = useMutation({
    mutationFn: () => createWalletMutation(config),
    onSuccess: () => {
      navigate({ to: '/setMarqetaPin' })
    },
    onError: (err: any) => {
      const title = 'Error activating instant card'
      const description =
        err?.response?.data?.meta?.errorDetail ||
        'There was an error activating your instant card. Please try again later.'

      showAlert({
        title,
        description,
        hideCancel: true,
      })
    },
  })
  return { mutate, isPending }
}

export const useCreateWalletMutationState = () => {
  const [data] = useMutationState<CreateWalletResponse>({
    filters: { mutationKey: [WALLET_CREATION_MUTATION_KEY] },
    select: mutation => {
      return mutation.state.data as CreateWalletResponse
    },
  })

  return data
}
